import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StrapiUser } from '../api/strapi';
import { strapiFetchMe, strapiLogin } from '../api/strapi';
import { strapiImpersonateUser } from '../api/strapiUsers';
import { queryKeys } from '../query/queryKeys';
import {
  IMPERSONATE_STRAPI_JWT_STORAGE_KEY,
  STRAPI_JWT_STORAGE_KEY,
  isImpersonationActive,
  readImpersonationBackupJwt,
} from './authStorage';

type AuthContextValue = {
  jwt: string | null;
  user: StrapiUser | null;
  /** True while restoring session or refetching /api/users/me */
  loading: boolean;
  /** True when `impersonate_strapi_jwt` holds the pre-impersonation admin token. */
  isImpersonating: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  loginPending: boolean;
  logout: () => void;
  setSession: (jwt: string | null, user: StrapiUser | null) => void;
  impersonateUser: (targetUserNumericId: string) => Promise<void>;
  impersonatePending: boolean;
  stopImpersonating: () => Promise<void>;
  stopImpersonatingPending: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function clearSessionQueries(queryClient: QueryClient) {
  queryClient.removeQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) &&
      q.queryKey[0] === 'strapi' &&
      (q.queryKey[1] === 'me' || q.queryKey[1] === 'navigationMenu'),
  });
}

function clearAllStrapiQueries(queryClient: QueryClient) {
  queryClient.removeQueries({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'strapi',
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [jwt, setJwtState] = useState<string | null>(() =>
    localStorage.getItem(STRAPI_JWT_STORAGE_KEY),
  );
  const [isImpersonating, setIsImpersonating] = useState(() => isImpersonationActive());

  const persistJwt = useCallback(
    (next: string | null, options?: { keepImpersonationBackup?: boolean }) => {
      if (next) localStorage.setItem(STRAPI_JWT_STORAGE_KEY, next);
      else {
        localStorage.removeItem(STRAPI_JWT_STORAGE_KEY);
        if (!options?.keepImpersonationBackup) {
          localStorage.removeItem(IMPERSONATE_STRAPI_JWT_STORAGE_KEY);
          setIsImpersonating(false);
        }
        clearSessionQueries(queryClient);
      }
      setJwtState(next);
    },
    [queryClient],
  );

  const applySessionJwt = useCallback(
    (nextJwt: string, nextUser: StrapiUser | null) => {
      localStorage.setItem(STRAPI_JWT_STORAGE_KEY, nextJwt);
      setJwtState(nextJwt);
      if (nextUser) {
        queryClient.setQueryData(queryKeys.me(nextJwt), nextUser);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.me(nextJwt) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.navigationMenu(nextJwt) });
    },
    [queryClient],
  );

  const meQuery = useQuery({
    queryKey: queryKeys.me(jwt),
    queryFn: () => strapiFetchMe(jwt!),
    enabled: !!jwt,
    staleTime: 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (!meQuery.isSuccess || !meQuery.data) return;
    if (meQuery.data.blocked) {
      persistJwt(null);
    }
  }, [meQuery.isSuccess, meQuery.data, persistJwt]);

  useEffect(() => {
    if (!meQuery.isError) return;
    persistJwt(null);
  }, [meQuery.isError, persistJwt]);

  const loginMutation = useMutation({
    mutationFn: async (vars: { identifier: string; password: string }) => {
      const res = await strapiLogin(vars.identifier, vars.password);
      if (res.user.blocked) {
        throw new Error('Your account is pending admin approval.');
      }
      return res;
    },
    onSuccess: (data) => {
      persistJwt(data.jwt);
      queryClient.setQueryData(queryKeys.me(data.jwt), data.user);
      void queryClient.invalidateQueries({ queryKey: queryKeys.me(data.jwt) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.navigationMenu(data.jwt) });
    },
  });

  const login = useCallback(
    async (identifier: string, password: string) => {
      await loginMutation.mutateAsync({ identifier, password });
    },
    [loginMutation],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(IMPERSONATE_STRAPI_JWT_STORAGE_KEY);
    setIsImpersonating(false);
    persistJwt(null);
    clearAllStrapiQueries(queryClient);
  }, [persistJwt, queryClient]);

  const impersonateMutation = useMutation({
    mutationFn: async (targetUserNumericId: string) => {
      if (!jwt) throw new Error('Not signed in');
      if (readImpersonationBackupJwt()) {
        throw new Error('Already impersonating. Exit impersonation before switching users.');
      }
      const adminJwt = jwt;
      const data = await strapiImpersonateUser(adminJwt, targetUserNumericId);
      return { adminJwt, data };
    },
    onSuccess: ({ adminJwt, data }) => {
      localStorage.setItem(IMPERSONATE_STRAPI_JWT_STORAGE_KEY, adminJwt);
      setIsImpersonating(true);
      clearAllStrapiQueries(queryClient);
      applySessionJwt(data.jwt, data.user);
      navigate('/dashboard', { replace: true });
    },
  });

  const impersonateUser = useCallback(
    async (targetUserNumericId: string) => {
      await impersonateMutation.mutateAsync(targetUserNumericId);
    },
    [impersonateMutation],
  );

  const stopImpersonateMutation = useMutation({
    mutationFn: async () => {
      const backup = readImpersonationBackupJwt();
      if (!backup) throw new Error('Not impersonating');
      const me = await strapiFetchMe(backup);
      return { backup, me };
    },
    onSuccess: ({ backup, me }) => {
      localStorage.removeItem(IMPERSONATE_STRAPI_JWT_STORAGE_KEY);
      setIsImpersonating(false);
      clearAllStrapiQueries(queryClient);
      applySessionJwt(backup, me);
      navigate('/superadmin/users', { replace: true });
    },
  });

  const stopImpersonating = useCallback(async () => {
    if (!readImpersonationBackupJwt()) {
      setIsImpersonating(false);
      return;
    }
    await stopImpersonateMutation.mutateAsync();
  }, [stopImpersonateMutation]);

  const setSession = useCallback(
    (nextJwt: string | null, nextUser: StrapiUser | null) => {
      if (nextJwt) {
        applySessionJwt(nextJwt, nextUser);
      } else {
        persistJwt(null);
      }
    },
    [applySessionJwt, persistJwt],
  );

  const user = jwt ? meQuery.data ?? null : null;
  const loading = !!jwt && meQuery.isLoading;

  const value = useMemo(
    () => ({
      jwt,
      user,
      loading,
      isImpersonating,
      login,
      loginPending: loginMutation.isPending,
      logout,
      setSession,
      impersonateUser,
      impersonatePending: impersonateMutation.isPending,
      stopImpersonating,
      stopImpersonatingPending: stopImpersonateMutation.isPending,
    }),
    [
      jwt,
      user,
      loading,
      isImpersonating,
      login,
      loginMutation.isPending,
      logout,
      setSession,
      impersonateUser,
      impersonateMutation.isPending,
      stopImpersonating,
      stopImpersonateMutation.isPending,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
